import { useState } from 'react';
import { Avatar, Grid, IconButton, Paper, SvgIcon, TextField, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { ShareDocument } from 'queries';
import { X } from 'ts-react-feather-icons';

type Props = {
  shared: Pick<ShareDocument, 'id' | 'sharedToEmail'>[] | undefined;
  emails: string[];
  setEmails: (emails: string[]) => void;
  onDelete: (id: string) => void;
  setComment: (comment: string) => void;
};

const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      paddingLeft: '5%',
      paddingRight: '5%',
      justifyContent: 'center',
    },
    container: {
      marginBottom: '15px',
      border: '1px solid #44D2F2',
      width: '100%',
      marginRight: '1.5rem',
      minHeight: '20vh',
      maxHeight: '20vh',
      overflow: 'auto',
    },
    comment: { width: '100%', marginRight: 'auto', marginBottom: '10px' },
    list: {
      display: 'flex',
      alignItems: 'center',
    },
    baseListItem: {
      [theme.breakpoints.up('md')]: {
        width: '65%',
      },
      width: 'auto',
      wordBreak: 'break-word',
      marginBottom: '.3rem',
      marginTop: '.5rem',
      marginLeft: '15px',
      display: 'flex',
      alignItems: 'center',
      borderRadius: '3rem',
    },
    newAddedItem: {
      [theme.breakpoints.up('sm')]: {
        width: '65%',
      },
      border: '1px solid #44D2F2',
      padding: '.5rem',
      background: '#F0F4F6',
    },
    existingItem: {
      border: '1px solid #434E59',
      boxSizing: 'border-box',
      padding: '.1rem',
      fontWeight: 'bold',
    },
    baseX: {
      borderRadius: '50%',
      padding: '.3rem',
    },
    newAddedItemX: {
      marginLeft: 'auto',
      border: '1px solid #434E59',
    },
    existingItemX: {
      background: 'black',
      color: 'white',
      marginLeft: '5px',
    },
    currentUsersTitle: {
      textAlign: 'left',
      marginTop: '24px',
      marginBottom: '10px',
    },
    currentUserAvatar: {
      marginLeft: '8px',
      margin: '5px',
    },
    sharedUserName: {
      padding: '10px',
    },
    newlySharedUserName: {
      marginLeft: '10px',
    },
    grid: {
      marginTop: '15px',
    },
  }),
);

export default function DocumentShare({ shared, emails, setEmails, onDelete, setComment }: Props) {
  const regex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  const classes = useStyles();
  const [input, setInput] = useState('');

  const onEmailKeyPress = () => {
    const newEmails = new Set(
      input.split(',').filter((email: string) => !emails.includes(email) && regex.test(email)),
    );
    setEmails([...emails, ...newEmails]);
    setInput('');
  };

  return (
    <div className={classes.root}>
      <Typography variant="h6">
        Who would you like to share this with? We will send them a link to view this document that
        expires within 30 days.
      </Typography>
      <Grid className={classes.grid} container spacing={2}>
        <Grid item md={6} xs={12}>
          <TextField
            value={input}
            className={classes.comment}
            label="Enter comma separated emails"
            //Tab only fires onKeyDown or onKeyUp, not onKeyPress
            onKeyDown={
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (e: any) => {
                if (e.key === 'Tab') {
                  onEmailKeyPress();
                }
              }
            }
            onKeyPress={
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (e: any) => {
                if (e.key === 'Enter') {
                  onEmailKeyPress();
                }
              }
            }
            onChange={(e) => {
              setInput(e.target.value);
            }}
          />
          <div className={classes.container}>
            {emails.map((recipient) => (
              <div key={recipient} className={classes.list}>
                <Paper elevation={3} className={clsx(classes.baseListItem, classes.newAddedItem)}>
                  <Typography className={classes.newlySharedUserName}>
                    {recipient}
                    <IconButton
                      tabIndex={-1}
                      style={{
                        float: 'right',
                        padding: '1px',
                        verticalAlign: 'middle',
                      }}
                      size="large"
                    ></IconButton>
                  </Typography>
                  <SvgIcon
                    fontSize="small"
                    className={clsx(classes.baseX, classes.newAddedItemX)}
                    onClick={() => setEmails(emails.filter((email) => email !== recipient))}
                  >
                    <X />
                  </SvgIcon>
                </Paper>
              </div>
            ))}
          </div>
        </Grid>
        <Grid item md={6} xs={12}>
          <Typography className={classes.currentUsersTitle}>
            Current users with ongoing access
          </Typography>
          <div className={classes.container}>
            {shared &&
              shared.map(({ sharedToEmail, id }) => (
                <div key={id} className={classes.list}>
                  <Paper elevation={3} className={clsx(classes.baseListItem, classes.existingItem)}>
                    <Avatar className={classes.currentUserAvatar} />
                    <Typography className={classes.sharedUserName}>
                      {sharedToEmail}
                      <IconButton
                        tabIndex={-1}
                        style={{
                          float: 'right',
                          padding: '1px',
                          verticalAlign: 'middle',
                        }}
                        size="large"
                      />
                    </Typography>
                  </Paper>
                  <SvgIcon
                    fontSize="small"
                    className={clsx(classes.baseX, classes.existingItemX)}
                    onClick={() => onDelete(id)}
                  >
                    <X />
                  </SvgIcon>
                </div>
              ))}
          </div>
        </Grid>
      </Grid>
      <form>
        <TextField
          className={classes.comment}
          onChange={(e) => setComment(e.target.value)}
          label="Message (Optional)"
        />
      </form>
    </div>
  );
}
